import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Building2, Globe, Sparkles, Check, AlertCircle,
  Trash2, ChevronDown, ChevronUp, Save, RefreshCw,
  Tag, Lightbulb, Target, Layers, UserCheck, BookOpen,
  ToggleLeft, ToggleRight, Plus, FileText, Users,
  Briefcase, GraduationCap, MessageSquare, Brain, X,
  ArrowLeft, Pencil, Star, ChevronRight,
} from 'lucide-react';
import {
  getStoredBrandProfile,
  saveBrandProfile,
  clearBrandProfile,
  getAllBrandProfiles,
  setActiveBrandProfile,
  deleteBrandProfileById,
  type BrandProfile,
  getKnowledgeEntries,
  saveKnowledgeEntry,
  deleteKnowledgeEntry,
  type KnowledgeEntry,
  type KnowledgeEntryType,
  getStoredGroqKey,
  getStoredGeminiKey,
} from '../services/openai';

const VOICE_STYLES = ['Executive', 'Technical Expert', 'Founder Voice', 'Conversational'] as const;

const VOICE_META: Record<string, { icon: string; desc: string }> = {
  'Executive': { icon: '📊', desc: 'ROI-focused · authoritative · outcome-driven' },
  'Technical Expert': { icon: '⚙️', desc: 'Logic-heavy · detailed · framework-aware' },
  'Founder Voice': { icon: '🚀', desc: 'Passionate · visionary · anecdotal' },
  'Conversational': { icon: '💬', desc: 'Short sentences · relatable · peer-to-peer' },
};

const HUMANIZATION_OPTIONS = [
  { key: 'personalExperience', label: 'Personal Experience', desc: '"In our experience working with…" — inject project log context', badge: 'Project Logs' },
  { key: 'storytelling', label: 'Storytelling', desc: 'Mini case studies, problem→solution narratives', badge: 'Customer Stories' },
  { key: 'industryAnecdotes', label: 'Industry Anecdotes & Expert Opinions', desc: 'Unique stances, lessons learned, fail-forward moments', badge: 'Expert Opinions' },
] as const;

const ENTRY_TYPES: Array<{ type: KnowledgeEntryType; label: string; icon: React.ReactNode; color: string; placeholder: string }> = [
  { type: 'customer_story', label: 'Customer Story', icon: <Users className="w-3.5 h-3.5" />, color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20', placeholder: 'e.g. Acme Corp reduced churn by 40% after deploying our AI…' },
  { type: 'project_log', label: 'Project Log', icon: <Briefcase className="w-3.5 h-3.5" />, color: 'text-violet-400 bg-violet-500/10 border-violet-500/20', placeholder: 'e.g. During our 2024 digital transformation for a 500-bed hospital…' },
  { type: 'lesson_learned', label: 'Lesson Learned', icon: <GraduationCap className="w-3.5 h-3.5" />, color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', placeholder: 'e.g. The biggest mistake we see in B2B SaaS onboarding is…' },
  { type: 'expert_opinion', label: 'Expert Opinion', icon: <Brain className="w-3.5 h-3.5" />, color: 'text-fuchsia-400 bg-fuchsia-500/10 border-fuchsia-500/20', placeholder: 'e.g. Our take on RAG vs. Fine-tuning: RAG wins for most enterprise use cases because…' },
  { type: 'text_snippet', label: 'Text Snippet', icon: <FileText className="w-3.5 h-3.5" />, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', placeholder: 'Any proprietary statistic, quote, or branded phrase you want AI to reference…' },
];

// Blank form state
const blankForm = () => ({
  id: '',
  url: '',
  companyName: '',
  products: '',
  industries: '',
  valueProps: '',
  differentiators: '',
  voiceStyle: 'Executive' as string,
  personalExperience: true,
  storytelling: true,
  industryAnecdotes: true,
});

type FormState = ReturnType<typeof blankForm>;

function profileToForm(p: BrandProfile & { id?: string }): FormState {
  return {
    id: p.id || '',
    url: p.websiteUrl || '',
    companyName: p.companyName || '',
    products: p.products?.join('\n') || '',
    industries: p.industriesServed?.join('\n') || '',
    valueProps: p.valueProps?.join('\n') || '',
    differentiators: p.differentiators?.join('\n') || '',
    voiceStyle: p.voiceStyle || 'Executive',
    personalExperience: p.enablePersonalExperience ?? true,
    storytelling: p.enableStorytelling ?? true,
    industryAnecdotes: p.enableAnecdotes ?? true,
  };
}

function formToProfile(f: FormState): BrandProfile & { id: string } {
  return {
    id: f.id || `bp_${Date.now()}`,
    companyName: f.companyName || f.url,
    websiteUrl: f.url,
    products: f.products.split('\n').map(s => s.trim()).filter(Boolean),
    industriesServed: f.industries.split('\n').map(s => s.trim()).filter(Boolean),
    valueProps: f.valueProps.split('\n').map(s => s.trim()).filter(Boolean),
    differentiators: f.differentiators.split('\n').map(s => s.trim()).filter(Boolean),
    voiceStyle: f.voiceStyle as BrandProfile['voiceStyle'],
    enablePersonalExperience: f.personalExperience,
    enableStorytelling: f.storytelling,
    enableAnecdotes: f.industryAnecdotes,
  };
}

// ── view modes ───────────────────────────────────────────────────────────────
type ViewMode = 'list' | 'edit' | 'new';

const BrandIntelligence: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'brand' | 'knowledge'>('brand');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Profile list
  const [profiles, setProfiles] = useState<(BrandProfile & { id: string })[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string>('');

  // Form for new/edit
  const [form, setForm] = useState<FormState>(blankForm());
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlError, setCrawlError] = useState<string | null>(null);
  const [crawlSuccess, setCrawlSuccess] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandDetails, setExpandDetails] = useState(true);

  // Knowledge Repository state
  const [entries, setEntries] = useState<KnowledgeEntry[]>(getKnowledgeEntries());
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntryType, setNewEntryType] = useState<KnowledgeEntryType>('customer_story');
  const [newEntryTitle, setNewEntryTitle] = useState('');
  const [newEntryContent, setNewEntryContent] = useState('');
  const [newEntryTags, setNewEntryTags] = useState('');
  const [filterType, setFilterType] = useState<KnowledgeEntryType | 'all'>('all');

  // Edit entry state
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editEntryContent, setEditEntryContent] = useState('');
  const [editEntryTitle, setEditEntryTitle] = useState('');

  const hasApiKey = !!(getStoredGroqKey() || getStoredGeminiKey() || true);

  // Load profiles on mount — also clean up any orphaned active profile from old sessions
  useEffect(() => {
    refreshProfiles();
    setEntries(getKnowledgeEntries());
  }, []);

  const refreshProfiles = () => {
    const list = getAllBrandProfiles();
    setProfiles(list);
    const active = getStoredBrandProfile() as (BrandProfile & { id?: string }) | null;
    if (active) {
      // If active profile has no id, it's legacy data — it won't appear in the list
      // Migrate it: if list already has it (by name), use that; otherwise clear it
      if (!active.id) {
        const match = list.find(p => p.companyName === active.companyName);
        if (match) {
          setActiveProfileId(match.id);
        } else {
          // Orphaned old profile — clear it so it doesn't show stale data
          clearBrandProfile();
        }
      } else {
        // Check the id still exists in the list
        const stillExists = list.some(p => p.id === active.id);
        if (stillExists) setActiveProfileId(active.id);
        else clearBrandProfile();
      }
    }
  };

  const handleClearActive = () => {
    clearBrandProfile();
    setActiveProfileId('');
    refreshProfiles();
  };

  // ── Direct brand extraction ────────────────────────────────────────────────
  const extractBrandDirect = async (targetUrl: string): Promise<{
    companyName?: string; products?: string[]; industriesServed?: string[];
    valuePropositions?: string[]; differentiators?: string[]; warning?: string;
  }> => {
    let siteContent = '';
    let scrapeWarning = '';
    try {
      let cleanUrl = targetUrl.trim();
      if (!/^https?:\/\//i.test(cleanUrl)) cleanUrl = 'https://' + cleanUrl;
      const jinaRes = await fetch(`https://r.jina.ai/${cleanUrl}`, {
        headers: { 'Accept': 'text/plain', 'X-Return-Format': 'text' },
        signal: AbortSignal.timeout(15000),
      });
      if (jinaRes.ok) {
        const text = await jinaRes.text();
        siteContent = text.replace(/\s+/g, ' ').trim().slice(0, 2500);
      }
    } catch { /* fallback below */ }

    if (!siteContent || siteContent.length < 100) {
      try {
        let cleanUrl = targetUrl.trim();
        if (!/^https?:\/\//i.test(cleanUrl)) cleanUrl = 'https://' + cleanUrl;
        const aoRes = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(cleanUrl)}`, {
          signal: AbortSignal.timeout(12000),
        });
        if (aoRes.ok) {
          const aoData = await aoRes.json() as { contents?: string };
          const html = aoData?.contents || '';
          siteContent = html
            .replace(/<script[\s\S]*?<\/script>/gi, ' ')
            .replace(/<style[\s\S]*?<\/style>/gi, ' ')
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ').trim().slice(0, 2500);
        }
      } catch { /* ignore */ }
    }

    if (!siteContent || siteContent.length < 50) {
      scrapeWarning = 'Could not scrape website content. Profile inferred from URL only.';
      siteContent = `Website URL: ${targetUrl}`;
    }

    // Compact prompt — keeps token usage low so response fits in budget
    const prompt = [
      'Extract brand profile. Respond with ONLY a JSON object — no markdown, no explanation.',
      `Content: ${siteContent}`,
      'JSON format (all fields required):',
      '{"companyName":"Co","products":["p1","p2"],"industriesServed":["i1"],"valuePropositions":["v1","v2","v3"],"differentiators":["d1","d2"]}',
    ].join('\n');

    let raw = '';
    const groqKey = localStorage.getItem('uxcf_groq_key') || '';
    const geminiKey = localStorage.getItem('uxcf_gemini_key') || '';
    const orKey = localStorage.getItem('uxcf_openrouter_key') || '';

    // ── Try Groq first (fast, reliable) ─────────────────────────────────────────
    if (groqKey && !raw) {
      try {
        for (const model of ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant']) {
          try {
            const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${groqKey}` },
              body: JSON.stringify({ model, temperature: 0.3, max_tokens: 1024, messages: [{ role: 'user', content: prompt }] }),
            });
            const d = await res.json() as { choices?: Array<{ message: { content: string } }>; error?: { message: string } };
            if (!res.ok) throw new Error(d?.error?.message ?? `Groq ${res.status}`);
            raw = d?.choices?.[0]?.message?.content ?? '';
            if (raw) break;
          } catch (e) {
            const m = String(e).toLowerCase();
            if (m.includes('rate limit') || m.includes('quota') || m.includes('429') || m.includes('tpm')) {
              await new Promise(r => setTimeout(r, 5000)); continue;
            }
            throw e;
          }
        }
      } catch (e) { console.warn('[brand] Groq failed:', e); }
    }

    // ── Gemini fallback ─────────────────────────────────────────────────────────
    if (geminiKey && !raw) {
      try {
        for (const model of ['gemini-2.0-flash', 'gemini-2.0-flash-lite']) {
          try {
            const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${geminiKey}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }], generationConfig: { temperature: 0.3, maxOutputTokens: 1024 } }),
            });
            const d = await res.json() as { candidates?: Array<{ content: { parts: Array<{ text: string }> } }>; error?: { message: string } };
            if (!res.ok) throw new Error(d?.error?.message ?? `Gemini ${res.status}`);
            raw = d?.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
            if (raw) break;
          } catch (e) {
            const m = String(e).toLowerCase();
            if (m.includes('quota') || m.includes('rate') || m.includes('429')) {
              await new Promise(r => setTimeout(r, 3000)); continue;
            }
            throw e;
          }
        }
      } catch (e) { console.warn('[brand] Gemini failed:', e); }
    }

    // ── OpenRouter fallback (free, no credit card) ─────────────────────────────
    if (orKey && !raw) {
      try {
        const freeModels = [
          'meta-llama/llama-3.3-70b-instruct:free',
          'meta-llama/llama-3.1-8b-instruct:free',
          'google/gemma-3-12b-it:free',
        ];
        for (const model of freeModels) {
          try {
            const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${orKey}`, 'HTTP-Referer': 'https://content-factory.app', 'X-Title': 'Content Intelligence Factory' },
              body: JSON.stringify({ model, temperature: 0.3, max_tokens: 1024, messages: [{ role: 'user', content: prompt }] }),
            });
            const d = await res.json() as { choices?: Array<{ message: { content: string } }>; error?: { message: string } };
            if (!res.ok) throw new Error(d?.error?.message ?? `OR ${res.status}`);
            raw = d?.choices?.[0]?.message?.content ?? '';
            if (raw) break;
          } catch { await new Promise(r => setTimeout(r, 2000)); continue; }
        }
      } catch (e) { console.warn('[brand] OpenRouter failed:', e); }
    }

    if (!raw) throw new Error('All AI providers failed. Please add a Groq, Gemini, or OpenRouter API key in Settings.');

    // ── Robust JSON extraction (handles fences, preamble, truncation) ───────────
    let parsed: { companyName?: string; products?: string[]; industriesServed?: string[]; valuePropositions?: string[]; differentiators?: string[] } | null = null;
    try {
      let s = raw.replace(/^```(?:json)?\s*/im, '').replace(/\s*```\s*$/im, '').trim();
      const start = s.indexOf('{');
      const end   = s.lastIndexOf('}');
      if (start !== -1 && end !== -1 && end > start) {
        s = s.slice(start, end + 1);
        try {
          parsed = JSON.parse(s);
        } catch {
          // Salvage truncated JSON by closing unclosed brackets/braces
          const openBrackets = (s.match(/\[/g) || []).length - (s.match(/\]/g) || []).length;
          const openBraces   = (s.match(/\{/g) || []).length - (s.match(/\}/g) || []).length;
          let fixed = s.replace(/,\s*$/, '');
          for (let i = 0; i < openBrackets; i++) fixed += ']';
          for (let i = 0; i < openBraces;   i++) fixed += '}';
          try { parsed = JSON.parse(fixed); } catch { /* give up */ }
        }
      }
    } catch { /* parsed remains null */ }

    if (!parsed) throw new Error('Could not parse AI response. Please try again or enter the profile manually.');
    return { ...parsed, warning: scrapeWarning || undefined };
  };

  // ── Crawl handler — populates current form fields ─────────────────────────
  const handleCrawl = async () => {
    if (!form.url.trim()) return;
    setCrawlError(null);
    setCrawlSuccess(false);
    setIsCrawling(true);
    try {
      const cfWorker = (import.meta.env.VITE_CF_BRAND_WORKER as string) || '';
      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) || '';
      const supabaseKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || '';

      let data: {
        companyName?: string; products?: string[]; industriesServed?: string[];
        valuePropositions?: string[]; differentiators?: string[];
        profile?: { companyName?: string; products?: string[]; industriesServed?: string[]; valuePropositions?: string[]; differentiators?: string[] };
        _scrapeWarning?: string; warning?: string; error?: string; success?: boolean;
      } | null = null;

      if (cfWorker) {
        try {
          const res = await fetch(cfWorker, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: form.url }) });
          if (res.ok) data = await res.json();
        } catch { /* fallback */ }
      }

      if (!data && supabaseUrl && supabaseKey) {
        try {
          const res = await fetch(`${supabaseUrl}/functions/v1/extract-brand-profile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'apikey': supabaseKey, 'Authorization': `Bearer ${supabaseKey}` },
            body: JSON.stringify({ url: form.url }),
          });
          if (res.ok) data = await res.json();
        } catch { /* fallback */ }
      }

      if (!data || data.error) {
        const direct = await extractBrandDirect(form.url);
        data = { ...direct, success: true };
      }

      if (!data) throw new Error('All extraction methods failed. Please fill in details manually.');
      if (data.error) throw new Error(data.error);

      const p = (data.success && data.profile) ? data.profile : data;

      // ── KEY FIX: populate form from the scraped URL — no hardcoded data ──
      setForm(prev => ({
        ...prev,
        companyName: p.companyName || prev.companyName,
        products: p.products?.length ? p.products.join('\n') : prev.products,
        industries: p.industriesServed?.length ? p.industriesServed.join('\n') : prev.industries,
        valueProps: p.valuePropositions?.length ? p.valuePropositions.join('\n') : prev.valueProps,
        differentiators: p.differentiators?.length ? p.differentiators.join('\n') : prev.differentiators,
      }));

      const warn = data._scrapeWarning || data.warning;
      if (warn) {
        setCrawlError(`⚠️ Partial extraction: ${warn.slice(0, 180)}. Review and fill in missing fields manually.`);
      }
      setCrawlSuccess(true);
      setExpandDetails(true);
    } catch (e) {
      setCrawlError(e instanceof Error ? e.message : 'Extraction failed. Please fill in the profile manually.');
    } finally {
      setIsCrawling(false);
    }
  };

  // ── Save form as profile ──────────────────────────────────────────────────
  const handleSave = () => {
    const profile = formToProfile(form);
    saveBrandProfile(profile);
    setActiveBrandProfile(profile.id);
    refreshProfiles();
    setSaved(true);
    setTimeout(() => { setSaved(false); setViewMode('list'); }, 1200);
  };

  // ── Open edit for an existing profile ────────────────────────────────────
  const handleEdit = (p: BrandProfile & { id: string }) => {
    setForm(profileToForm(p));
    setCrawlError(null);
    setCrawlSuccess(false);
    setViewMode('edit');
    setExpandDetails(true);
  };

  // ── Start new profile ─────────────────────────────────────────────────────
  const handleNew = () => {
    setForm(blankForm());
    setCrawlError(null);
    setCrawlSuccess(false);
    setViewMode('new');
    setExpandDetails(true);
  };

  // ── Delete profile ────────────────────────────────────────────────────────
  const handleDelete = (id: string) => {
    deleteBrandProfileById(id);
    refreshProfiles();
  };

  // ── Activate profile ──────────────────────────────────────────────────────
  const handleActivate = (id: string) => {
    setActiveBrandProfile(id);
    setActiveProfileId(id);
    refreshProfiles();
  };

  // ── Knowledge Repository handlers ─────────────────────────────────────────
  const handleAddEntry = () => {
    if (!newEntryTitle.trim() || !newEntryContent.trim()) return;
    const entry: KnowledgeEntry = {
      id: `ke_${Date.now()}`,
      type: newEntryType,
      title: newEntryTitle.trim(),
      content: newEntryContent.trim(),
      tags: newEntryTags.split(',').map(t => t.trim()).filter(Boolean),
      createdAt: new Date().toISOString(),
    };
    saveKnowledgeEntry(entry);
    setEntries(getKnowledgeEntries());
    setNewEntryTitle(''); setNewEntryContent(''); setNewEntryTags('');
    setShowAddForm(false);
  };

  const handleDeleteEntry = (id: string) => {
    deleteKnowledgeEntry(id);
    setEntries(getKnowledgeEntries());
  };

  const handleEditEntry = (entry: KnowledgeEntry) => {
    setEditingEntryId(entry.id);
    setEditEntryTitle(entry.title);
    setEditEntryContent(entry.content);
  };

  const handleSaveEditEntry = (id: string) => {
    const updated = entries.map(e =>
      e.id === id ? { ...e, title: editEntryTitle, content: editEntryContent } : e
    );
    updated.forEach(e => saveKnowledgeEntry(e));
    setEntries(getKnowledgeEntries());
    setEditingEntryId(null);
  };

  const filteredEntries = filterType === 'all' ? entries : entries.filter(e => e.type === filterType);
  const getEntryMeta = (type: KnowledgeEntryType) => ENTRY_TYPES.find(e => e.type === type);
  const activeProfile = getStoredBrandProfile() as (BrandProfile & { id?: string }) | null;

  const completionPct = [
    form.companyName.trim(),
    form.products.trim(),
    form.industries.trim(),
    form.valueProps.trim(),
    form.differentiators.trim(),
  ].filter(Boolean).length * 20;

  return (
    <Layout>
    <div className="p-4 sm:p-6 space-y-6 max-w-3xl mx-auto" style={{ minHeight: '100%', background: 'linear-gradient(135deg, #07070f 0%, #0a0a14 100%)' }}>
      {/* ── Top Nav Bar ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
        {(viewMode === 'edit' || viewMode === 'new') && (
          <>
            <button
              onClick={() => setViewMode('list')}
              className="text-slate-400 hover:text-slate-200 text-sm transition-colors"
            >
              Brand Profiles
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
          </>
        )}
        <span className="text-slate-200 text-sm font-medium">
          {viewMode === 'new' ? 'New Profile' : viewMode === 'edit' ? 'Edit Profile' : 'Brand & Knowledge Hub'}
        </span>
      </div>

      {/* ── Page Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-violet-400 text-sm font-medium mb-1">
            <Building2 className="w-4 h-4" />
            <span>Brand Intelligence Layer</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-100">Brand &amp; Knowledge Hub</h1>
          <p className="text-slate-400 text-sm mt-1">
            Onboard company data and proprietary knowledge. AI agents inject your brand context, stories, and expertise into every content bundle.
          </p>
        </div>
      </div>

      {/* ── Status Row ── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-wider font-medium text-slate-500">Active Profile</p>
            {activeProfile && (
              <button
                onClick={handleClearActive}
                title="Clear active profile"
                className="text-[10px] text-slate-600 hover:text-red-400 transition-colors flex items-center gap-0.5"
              >
                <X className="w-2.5 h-2.5" /> Clear
              </button>
            )}
          </div>
          <p className={`text-sm font-semibold truncate ${activeProfile ? 'text-emerald-400' : 'text-slate-500'}`}>
            {activeProfile ? `✓ ${activeProfile.companyName || 'Unnamed'}` : 'None active'}
          </p>
        </div>
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-500 mb-1">Saved Profiles</p>
          <p className={`text-sm font-semibold ${profiles.length > 0 ? 'text-cyan-400' : 'text-slate-500'}`}>
            {profiles.length > 0 ? `${profiles.length} profile${profiles.length !== 1 ? 's' : ''}` : 'None saved'}
          </p>
        </div>
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-500 mb-1">Knowledge Entries</p>
          <p className={`text-sm font-semibold ${entries.length > 0 ? 'text-violet-300' : 'text-slate-500'}`}>
            {entries.length > 0 ? `${entries.length} entries` : 'Empty'}
          </p>
        </div>
      </div>

      {/* ── Tab Nav ── */}
      {viewMode === 'list' && (
        <div className="flex gap-1 bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-1">
          {[
            { key: 'brand', label: 'Brand Profiles', icon: <Building2 className="w-3.5 h-3.5" /> },
            { key: 'knowledge', label: 'Knowledge Repository', icon: <BookOpen className="w-3.5 h-3.5" /> },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as 'brand' | 'knowledge')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ════════════════════════════════════════════════
          PROFILE LIST VIEW
      ════════════════════════════════════════════════ */}
      {viewMode === 'list' && activeTab === 'brand' && (
        <div className="space-y-4">
          {/* Add Profile Button */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {profiles.length === 0 ? 'No brand profiles saved yet.' : `${profiles.length} profile${profiles.length !== 1 ? 's' : ''} saved`}
            </p>
            <button
              onClick={handleNew}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-xl transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Add Profile
            </button>
          </div>

          {profiles.length === 0 ? (
            <div className="text-center py-16 space-y-4 bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-2xl">
              <Building2 className="w-10 h-10 text-slate-700 mx-auto" />
              <div>
                <p className="text-slate-400 text-sm font-medium">No brand profiles yet</p>
                <p className="text-slate-600 text-xs mt-1">Add a profile to inject brand context into every content bundle.</p>
              </div>
              <button onClick={handleNew}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-xl transition-all"
              >
                <Plus className="w-3.5 h-3.5" /> Create First Profile
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {profiles.map(p => {
                const isActive = p.id === activeProfileId || p.id === (activeProfile as (BrandProfile & { id?: string }) | null)?.id;
                return (
                  <motion.div key={p.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className={`bg-[#12121a]/70 backdrop-blur-sm border rounded-2xl p-4 transition-all ${isActive ? 'border-violet-500/35 shadow-[0_0_12px_rgba(139,92,246,0.08)]' : 'border-white/[0.06] hover:border-white/[0.10]'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          {isActive && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-violet-500/15 text-violet-300 border border-violet-500/25 rounded-full text-[10px] font-semibold">
                              <Star className="w-2.5 h-2.5" /> Active
                            </span>
                          )}
                          <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">{p.voiceStyle || 'Executive'}</span>
                        </div>
                        <p className="text-sm font-bold text-slate-100 truncate">{p.companyName || 'Unnamed Profile'}</p>
                        {p.websiteUrl && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{p.websiteUrl}</p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {p.industriesServed?.slice(0, 3).map(ind => (
                            <span key={ind} className="px-1.5 py-0.5 bg-slate-800 text-slate-500 text-[10px] rounded">{ind}</span>
                          ))}
                          {p.products?.slice(0, 2).map(prod => (
                            <span key={prod} className="px-1.5 py-0.5 bg-slate-800 text-slate-500 text-[10px] rounded">{prod}</span>
                          ))}
                        </div>
                      </div>
                      {/* Action buttons */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {!isActive && (
                          <button onClick={() => handleActivate(p.id)}
                            title="Set as active"
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-violet-600/20 hover:bg-violet-600/40 text-violet-300 text-xs rounded-lg transition-colors"
                          >
                            <Star className="w-3 h-3" /> Use
                          </button>
                        )}
                        <button onClick={() => handleEdit(p)}
                          title="Edit profile"
                          className="p-1.5 text-slate-500 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(p.id)}
                          title="Delete profile"
                          className="p-1.5 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Active intelligence tip */}
          {activeProfile && (
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4 text-sm text-violet-300 flex items-start gap-2">
              <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Brand Intelligence active — <strong>{activeProfile.companyName}</strong> context + <strong>{activeProfile.voiceStyle}</strong> voice injected into all new content bundles.</span>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════
          NEW / EDIT FORM VIEW
      ════════════════════════════════════════════════ */}
      {(viewMode === 'new' || viewMode === 'edit') && (
        <div className="space-y-5">
          {/* Back button */}
          <button onClick={() => setViewMode('list')}
            className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to profiles
          </button>

          {/* Completion Bar */}
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400">Profile Completeness</span>
              <span className={completionPct >= 80 ? 'text-emerald-400' : completionPct >= 40 ? 'text-amber-400' : 'text-slate-500'}>{completionPct}%</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500"
                initial={{ width: 0 }} animate={{ width: `${completionPct}%` }} transition={{ duration: 0.4 }}
              />
            </div>
          </div>

          {/* Auto-Crawl */}
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-cyan-400" />
              <h2 className="text-sm font-semibold text-slate-200">Auto-Crawl Company Website</h2>
            </div>
            <p className="text-xs text-slate-500">
              Enter your company URL. AI will scrape the site and extract products, industry, value propositions, and differentiators automatically. Results populate the fields below — review and adjust as needed.
            </p>
            <div className="flex gap-2">
              <input
                type="url"
                value={form.url}
                onChange={e => setForm(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://yourcompany.com"
              className="flex-1 bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors"
              />
              <button
                onClick={handleCrawl}
                disabled={isCrawling || !form.url.trim() || !hasApiKey}
                className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-all"
              >
                {isCrawling ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {isCrawling ? 'Analysing…' : 'Extract Profile'}
              </button>
            </div>
            {crawlError && (
              <div className={`flex items-start gap-2 p-3 rounded-xl text-xs ${crawlError.startsWith('⚠️') ? 'bg-amber-500/10 border border-amber-500/30 text-amber-300' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {crawlError}
              </div>
            )}
            {crawlSuccess && (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-xs text-emerald-400">
                <Check className="w-3.5 h-3.5" /> Profile extracted from <strong>{form.url}</strong>! Review the fields below and save.
              </div>
            )}
          </div>

          {/* Profile Details Accordion */}
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl overflow-hidden">
            <button
              onClick={() => setExpandDetails(v => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
            >
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-semibold text-slate-200">Company Profile Details</span>
                {completionPct > 0 && (
                  <span className="px-2 py-0.5 bg-violet-500/10 text-violet-300 text-xs rounded-full">{completionPct}% complete</span>
                )}
              </div>
              {expandDetails ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {expandDetails && (
              <div className="px-5 pb-5 space-y-4 border-t border-white/[0.05]">
                <div className="space-y-1.5 pt-4">
                  <label className="text-xs text-slate-400 uppercase tracking-wider font-medium flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> Company Name
                  </label>
                  <input value={form.companyName} onChange={e => setForm(p => ({ ...p, companyName: e.target.value }))}
                    placeholder="e.g. Acme Corp"
                    className="w-full bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-400 uppercase tracking-wider font-medium flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" /> Products / Services (one per line)
                  </label>
                  <textarea rows={3} value={form.products} onChange={e => setForm(p => ({ ...p, products: e.target.value }))}
                    placeholder={"SaaS analytics platform\nAPI integration suite\nManaged consulting services"}
                    className="w-full bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors resize-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-400 uppercase tracking-wider font-medium flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5" /> Industries Served (one per line)
                  </label>
                  <textarea rows={2} value={form.industries} onChange={e => setForm(p => ({ ...p, industries: e.target.value }))}
                    placeholder={"Financial services\nHealthcare\nRetail & e-commerce"}
                    className="w-full bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors resize-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-400 uppercase tracking-wider font-medium flex items-center gap-1.5">
                    <Lightbulb className="w-3.5 h-3.5" /> Value Propositions (one per line)
                  </label>
                  <textarea rows={3} value={form.valueProps} onChange={e => setForm(p => ({ ...p, valueProps: e.target.value }))}
                    placeholder={"Reduce reporting time by 60%\nNo-code setup in under 30 minutes\nSOC 2 Type II certified"}
                    className="w-full bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors resize-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-400 uppercase tracking-wider font-medium flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5" /> Differentiators (one per line)
                  </label>
                  <textarea rows={2} value={form.differentiators} onChange={e => setForm(p => ({ ...p, differentiators: e.target.value }))}
                    placeholder={"Real-time AI predictions vs batch processing\nDedicated CSM included in all plans"}
                    className="w-full bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors resize-none" />
                </div>
              </div>
            )}
          </div>

          {/* Voice & Humanization */}
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-5 space-y-5">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-violet-400" />
              <h2 className="text-sm font-semibold text-slate-200">Voice Persona &amp; Humanization Layer</h2>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-slate-400 uppercase tracking-wider font-medium">Author Voice Persona</label>
              <div className="grid grid-cols-2 gap-2">
                {VOICE_STYLES.map(vs => (
                  <button key={vs} onClick={() => setForm(p => ({ ...p, voiceStyle: vs }))}
                    className={`px-3 py-2.5 rounded-xl text-xs font-medium border transition-all text-left ${
                      form.voiceStyle === vs
                        ? 'bg-violet-600/20 border-violet-500/60 text-violet-300 shadow-[0_0_8px_rgba(139,92,246,0.12)]'
                        : 'bg-[#0f0f1a] border-white/[0.07] text-slate-400 hover:border-white/[0.12]'
                    }`}
                  >
                    <span className="block font-semibold">{VOICE_META[vs]?.icon} {vs}</span>
                    <span className="block text-[10px] mt-0.5 text-slate-500">{VOICE_META[vs]?.desc}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-slate-400 uppercase tracking-wider font-medium flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Humanization Injection Elements
              </label>
              <div className="space-y-2">
                {HUMANIZATION_OPTIONS.map(opt => {
                  const isOn = form[opt.key as keyof FormState] as boolean;
                  return (
                    <button key={opt.key}
                      onClick={() => setForm(p => ({ ...p, [opt.key]: !p[opt.key as keyof FormState] }))}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                        isOn ? 'bg-violet-500/10 border-violet-500/25 text-slate-200' : 'bg-[#0f0f1a] border-white/[0.06] text-slate-500'
                      }`}
                    >
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{opt.label}</p>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] border ${isOn ? 'bg-violet-500/10 border-violet-500/20 text-violet-400' : 'bg-slate-700 border-slate-600 text-slate-500'}`}>
                            {opt.badge}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                      </div>
                      {isOn
                        ? <ToggleRight className="w-5 h-5 text-violet-400 flex-shrink-0" />
                        : <ToggleLeft className="w-5 h-5 text-slate-600 flex-shrink-0" />
                      }
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Save / Cancel */}
          <div className="flex items-center gap-3 pb-6">
            <button onClick={handleSave}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold rounded-xl text-sm transition-all shadow-lg shadow-violet-600/20"
            >
              {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saved ? 'Saved!' : viewMode === 'edit' ? 'Update Profile' : 'Save Brand Profile'}
            </button>
            <button onClick={() => setViewMode('list')}
              className="flex items-center gap-2 px-4 py-3 bg-[#0f0f1a] hover:bg-white/[0.06] border border-white/[0.07] text-slate-400 rounded-xl text-sm transition-colors"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
            {viewMode === 'edit' && (
              <button onClick={() => { handleDelete(form.id); setViewMode('list'); }}
                className="flex items-center gap-2 px-4 py-3 bg-[#0f0f1a] hover:bg-red-500/10 hover:text-red-400 text-slate-500 border border-white/[0.07] rounded-xl text-sm transition-colors ml-auto"
              >
                <Trash2 className="w-4 h-4" /> Delete Profile
              </button>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════
          KNOWLEDGE REPOSITORY TAB
      ════════════════════════════════════════════════ */}
      {viewMode === 'list' && activeTab === 'knowledge' && (
        <div className="space-y-5">
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-cyan-400" />
              <h2 className="text-sm font-semibold text-slate-200">Proprietary Knowledge Base</h2>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Store your proprietary intellectual property here — customer wins, project experiences, lessons learned, and expert stances. When humanization toggles are enabled, the AI will cross-reference this repository and inject authentic, competitor-proof context into every content bundle.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              {ENTRY_TYPES.map(et => (
                <span key={et.type} className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${et.color}`}>
                  {et.icon} {et.label}
                </span>
              ))}
            </div>
          </div>

          {/* Filter + Add */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex-1 flex gap-1 overflow-x-auto">
              {(['all', ...ENTRY_TYPES.map(e => e.type)] as const).map(t => (
                <button key={t}
                  onClick={() => setFilterType(t as KnowledgeEntryType | 'all')}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    filterType === t ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {t === 'all' ? `All (${entries.length})` : ENTRY_TYPES.find(e => e.type === t)?.label}
                  {t !== 'all' && ` (${entries.filter(e => e.type === t).length})`}
                </button>
              ))}
            </div>
            <button onClick={() => setShowAddForm(v => !v)}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium rounded-lg transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Add Entry
            </button>
          </div>

          {/* Add Form */}
          <AnimatePresence>
            {showAddForm && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="bg-[#12121a]/90 backdrop-blur-sm border border-violet-500/25 rounded-xl p-5 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-200">New Knowledge Entry</h3>
                  <button onClick={() => setShowAddForm(false)} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ENTRY_TYPES.map(et => (
                    <button key={et.type} onClick={() => setNewEntryType(et.type)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                        newEntryType === et.type ? `${et.color} border-current` : 'bg-slate-800 border-slate-700 text-slate-400'
                      }`}
                    >
                      {et.icon} {et.label}
                    </button>
                  ))}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-400 uppercase tracking-wider font-medium">Title / Name</label>
                  <input value={newEntryTitle} onChange={e => setNewEntryTitle(e.target.value)}
                    placeholder="e.g. Acme Corp — 40% churn reduction case study"
                    className="w-full bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-400 uppercase tracking-wider font-medium">Content</label>
                  <textarea rows={4} value={newEntryContent} onChange={e => setNewEntryContent(e.target.value)}
                    placeholder={ENTRY_TYPES.find(e => e.type === newEntryType)?.placeholder}
                    className="w-full bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors resize-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-400 uppercase tracking-wider font-medium">Tags (comma-separated)</label>
                  <input value={newEntryTags} onChange={e => setNewEntryTags(e.target.value)}
                    placeholder="healthcare, digital transformation, ROI"
                    className="w-full bg-[#0f0f1a] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500/60 transition-colors"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={handleAddEntry} disabled={!newEntryTitle.trim() || !newEntryContent.trim()}
                    className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-all"
                  >
                    <Check className="w-4 h-4" /> Save Entry
                  </button>
                  <button onClick={() => setShowAddForm(false)}
                    className="px-4 py-2.5 bg-[#0f0f1a] hover:bg-white/[0.06] border border-white/[0.07] text-slate-400 text-sm rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Entry List */}
          {filteredEntries.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <BookOpen className="w-8 h-8 text-slate-700 mx-auto" />
              <p className="text-slate-500 text-sm">No knowledge entries yet.</p>
              <p className="text-slate-600 text-xs">Add customer stories, project logs, and expert opinions to boost content humanization.</p>
              <button onClick={() => setShowAddForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm rounded-xl mx-auto transition-all"
              >
                <Plus className="w-3.5 h-3.5" /> Add First Entry
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEntries.map(entry => {
                const meta = getEntryMeta(entry.type);
                const isEditing = editingEntryId === entry.id;
                return (
                  <motion.div key={entry.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="bg-[#12121a]/70 backdrop-blur-sm border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.10] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${meta?.color}`}>
                            {meta?.icon} {meta?.label}
                          </span>
                          {entry.tags.map(tag => (
                            <span key={tag} className="px-1.5 py-0.5 bg-slate-800 text-slate-500 text-[10px] rounded">{tag}</span>
                          ))}
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <input value={editEntryTitle} onChange={e => setEditEntryTitle(e.target.value)}
                              className="w-full bg-[#0f0f1a] border border-violet-500/40 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none" />
                            <textarea rows={3} value={editEntryContent} onChange={e => setEditEntryContent(e.target.value)}
                              className="w-full bg-[#0f0f1a] border border-violet-500/40 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none resize-none" />
                            <div className="flex gap-2">
                              <button onClick={() => handleSaveEditEntry(entry.id)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs rounded-lg transition-colors"
                              >
                                <Check className="w-3 h-3" /> Save
                              </button>
                              <button onClick={() => setEditingEntryId(null)}
                                className="px-3 py-1.5 bg-[#0f0f1a] text-slate-400 text-xs rounded-lg transition-colors hover:bg-white/[0.06] border border-white/[0.07]"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-semibold text-slate-200 mb-1">{entry.title}</p>
                            <p className="text-xs text-slate-500 line-clamp-2">{entry.content}</p>
                          </>
                        )}
                      </div>
                      {!isEditing && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => handleEditEntry(entry)}
                            className="p-1.5 text-slate-600 hover:text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDeleteEntry(entry.id)}
                            className="p-1.5 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-700 mt-2">{new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Humanization Engine status */}
          <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-xl p-4">
            <h3 className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-3">Humanization Engine — Cross-Reference Logic</h3>
            <div className="space-y-2">
              {[
                { toggle: 'Personal Experiences', trigger: activeProfile?.enablePersonalExperience ?? false, action: 'Scans Project Logs', inject: '"During a recent project involving [X], we observed…"' },
                { toggle: 'Storytelling', trigger: activeProfile?.enableStorytelling ?? false, action: 'Scans Customer Stories', inject: '"This reminds us of a client who faced [Problem] and achieved [Result] by…"' },
                { toggle: 'Lessons Learned', trigger: activeProfile?.enableAnecdotes ?? false, action: 'Scans Expert Opinions', inject: '"The biggest takeaway from our years in [Industry] is…"' },
              ].map(row => (
                <div key={row.toggle} className={`flex items-start gap-3 p-3 rounded-xl border ${row.trigger ? 'bg-violet-500/5 border-violet-500/20' : 'bg-[#0f0f1a]/60 border-white/[0.05]'}`}>
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${row.trigger ? 'bg-violet-400' : 'bg-slate-700'}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${row.trigger ? 'text-slate-200' : 'text-slate-600'}`}>{row.toggle}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${row.trigger ? 'bg-violet-500/10 text-violet-400' : 'bg-slate-800 text-slate-600'}`}>{row.trigger ? 'ON' : 'OFF'}</span>
                    </div>
                    {row.trigger && <p className="text-[10px] text-slate-500 mt-0.5">{row.action} → {row.inject}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default BrandIntelligence;